import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { Comment, ModerationQueueItem, Topic } from '@mmf/contracts';
import {
  approveModerationItem,
  editModerationItem,
  fetchComment,
  fetchModerationQueue,
  fetchTopic,
  rejectModerationItem,
} from '../api/client';

/**
 * Moderator or Administrator. Lists two different kinds of item, both
 * needing a decision: PendingReview (Block-severity match, or a report —
 * held, not visible to anyone but its author yet) and Published
 * (Flag-severity match — already visible to everyone, just waiting on
 * review). See ModerationService.classify/approve/reject for how the two
 * are told apart and why Reject means something different for each.
 */
export function ModerationQueue(): React.JSX.Element {
  const queryClient = useQueryClient();

  const queue = useQuery({ queryKey: ['admin', 'moderation', 'queue'], queryFn: fetchModerationQueue });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'moderation', 'queue'] });
  };

  if (queue.isLoading) {
    return <p role="status">Loading the moderation queue…</p>;
  }

  if (queue.error) {
    return (
      <p role="alert" data-testid="queue-error">
        Could not load the moderation queue.
      </p>
    );
  }

  return (
    <section aria-labelledby="moderation-queue-heading" data-testid="moderation-queue">
      <h3 id="moderation-queue-heading">Moderation queue</h3>

      {queue.data && queue.data.length === 0 && (
        <p className="hint">Nothing is waiting for review.</p>
      )}

      <ul className="moderation-queue-list">
        {queue.data?.map((item) => <QueueRow key={`${item.targetType}-${item.targetId}`} item={item} onDone={invalidate} />)}
      </ul>
    </section>
  );
}

function QueueRow({
  item,
  onDone,
}: {
  item: ModerationQueueItem;
  onDone: () => Promise<void>;
}): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false);

  const approve = useMutation({
    mutationFn: () => approveModerationItem(item.targetType, item.targetId),
    onSuccess: onDone,
  });
  const reject = useMutation({
    mutationFn: () => rejectModerationItem(item.targetType, item.targetId),
    onSuccess: onDone,
  });

  const busy = approve.isPending || reject.isPending;

  return (
    <li className="moderation-queue-row" data-testid={`queue-item-${item.targetType}-${item.targetId}`}>
      <p className="hint">
        {item.targetType} by {item.authorDisplayName} ·{' '}
        <Link to={`/topics/${item.topicId}`}>view thread</Link>
      </p>
      {item.moderationState === 'Published' ? (
        <p className="notice warning" data-testid={`visibility-${item.targetType}-${item.targetId}`}>
          Already visible to everyone — flagged for review. Reject will take it down.
        </p>
      ) : (
        <p className="notice" data-testid={`visibility-${item.targetType}-${item.targetId}`}>
          Held — not visible to anyone but the author yet.
        </p>
      )}

      {isEditing ? (
        <QueueRowEditForm
          item={item}
          onCancel={() => setIsEditing(false)}
          onSaved={async () => {
            setIsEditing(false);
            await onDone();
          }}
        />
      ) : (
        <>
          <p className="excerpt">{item.excerpt}</p>
          {item.matchedPatterns.length > 0 && (
            <p className="hint">Matched: {item.matchedPatterns.join(', ')}</p>
          )}
          {item.reportReasons.length > 0 && (
            <p className="hint" data-testid={`report-reasons-${item.targetType}-${item.targetId}`}>
              Reported by {item.reportReasons.length} member{item.reportReasons.length === 1 ? '' : 's'}:{' '}
              {item.reportReasons.join(' · ')}
            </p>
          )}

          {(approve.error || reject.error) && (
            <p role="alert" className="notice warning">
              That action failed. Try again.
            </p>
          )}

          <div className="row">
            <button
              type="button"
              className="button small"
              disabled={busy}
              onClick={() => approve.mutate()}
              data-testid={`approve-${item.targetType}-${item.targetId}`}
            >
              Approve
            </button>
            <button
              type="button"
              className="button secondary small"
              disabled={busy}
              onClick={() => reject.mutate()}
              data-testid={`reject-${item.targetType}-${item.targetId}`}
            >
              Reject
            </button>
            <button
              type="button"
              className="button secondary small"
              disabled={busy}
              onClick={() => setIsEditing(true)}
              data-testid={`edit-queue-item-${item.targetType}-${item.targetId}`}
            >
              Edit
            </button>
          </div>
        </>
      )}
    </li>
  );
}

/**
 * Loads the full content on demand (the queue row itself only ever has a
 * 200-character excerpt) — editing a truncated excerpt would silently lose
 * whatever came after it.
 */
function QueueRowEditForm({
  item,
  onCancel,
  onSaved,
}: {
  item: ModerationQueueItem;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const isTopic = item.targetType === 'Topic';

  const content = useQuery<Topic | Comment>({
    queryKey: ['admin', 'moderation', 'edit-source', item.targetType, item.targetId],
    queryFn: (): Promise<Topic | Comment> =>
      isTopic ? fetchTopic(item.targetId) : fetchComment(item.targetId),
  });

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  // Populate the form once, the moment content actually arrives — a plain
  // `[item.targetId]` dependency would run too early (before the async
  // fetch resolves) and never again; a plain `[content.data]` dependency
  // would re-run on every background refetch and stomp on an in-progress
  // edit. This runs exactly once per row.
  const hasInitialised = useRef(false);

  useEffect(() => {
    if (content.data && !hasInitialised.current) {
      setTitle('title' in content.data ? content.data.title : '');
      setBody(content.data.body);
      hasInitialised.current = true;
    }
  }, [content.data]);

  const save = useMutation({
    mutationFn: () =>
      editModerationItem(item.targetType, item.targetId, isTopic ? { title, body } : { body }),
    onSuccess: onSaved,
  });

  if (content.isLoading) {
    return <p role="status">Loading the full content to edit…</p>;
  }

  if (content.error || !content.data) {
    return (
      <p role="alert" className="notice warning">
        Could not load the full content. Try again.
      </p>
    );
  }

  return (
    <form
      className="new-topic-form"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {isTopic && (
        <>
          <label htmlFor={`edit-queue-title-${item.targetId}`}>Title</label>
          <input
            id={`edit-queue-title-${item.targetId}`}
            value={title}
            required
            onChange={(event) => setTitle(event.target.value)}
          />
        </>
      )}

      <label htmlFor={`edit-queue-body-${item.targetId}`}>Body</label>
      <textarea
        id={`edit-queue-body-${item.targetId}`}
        value={body}
        rows={5}
        required
        onChange={(event) => setBody(event.target.value)}
      />

      {save.error && (
        <p role="alert" className="notice warning">
          Could not save your changes. Try again.
        </p>
      )}

      <div className="row">
        <button
          type="submit"
          className="button small"
          disabled={save.isPending || body.trim() === '' || (isTopic && title.trim() === '')}
          data-testid={`save-queue-edit-${item.targetType}-${item.targetId}`}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="button secondary small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
